import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CreditCard, Truck, ArrowLeft, Loader2 } from 'lucide-react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { useLanguage } from '../contexts/LanguageContext';
import { useCart } from '../contexts/CartContext';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PAYPAL_CLIENT_ID = 'sb'; // Sandbox client ID for testing

export default function Checkout() {
  const { t, getLocalizedField } = useLanguage();
  const { items, subtotal, clearCart } = useCart();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    delivery_address: '',
    delivery_city: '',
    delivery_postal: '',
    payment_method: 'stripe',
  });
  
  const [deliveryFee, setDeliveryFee] = useState(5.50);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPayPal, setShowPayPal] = useState(false);
  const [orderId, setOrderId] = useState(null);
  
  // Calculate delivery fee based on city
  useEffect(() => {
    const city = formData.delivery_city.toLowerCase();
    if (city.includes('liège') || city.includes('liege')) {
      setDeliveryFee(5.50);
    } else if (['seraing', 'herstal', 'ans', 'flémalle', 'flemalle', 'chaudfontaine', 'grace-hollogne'].some(c => city.includes(c))) {
      setDeliveryFee(7.50);
    } else if (city) {
      setDeliveryFee(14.50);
    }
  }, [formData.delivery_city]);
  
  const total = subtotal + deliveryFee;
  
  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
    
    if (e.target.name === 'payment_method') {
      setShowPayPal(e.target.value === 'paypal');
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (items.length === 0) {
      toast.error('Votre panier est vide');
      return;
    }
    
    // Validate minimum order for outside Liège
    const city = formData.delivery_city.toLowerCase();
    const isOutsideLiege = !city.includes('liège') && !city.includes('liege') && 
      !['seraing', 'herstal', 'ans', 'flémalle', 'flemalle', 'chaudfontaine', 'grace-hollogne'].some(c => city.includes(c));
    
    if (isOutsideLiege && subtotal < 75) {
      toast.error('Commande minimum de 75€ requise pour la livraison hors Liège');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const orderData = {
        items: items.map(item => ({
          product_id: item.product.id,
          quantity: item.quantity
        })),
        ...formData,
        origin_url: window.location.origin
      };
      
      const response = await axios.post(`${API}/orders`, orderData);
      
      if (formData.payment_method === 'stripe' && response.data.checkout_url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.checkout_url;
      } else if (formData.payment_method === 'paypal') {
        setOrderId(response.data.order_id);
        setShowPayPal(true);
      } else if (formData.payment_method === 'cash') {
        clearCart();
        toast.success('Commande confirmée ! Paiement à la livraison.');
        navigate('/checkout/success?method=cash');
      }
    } catch (error) {
      // Error handled silently
      toast.error(error.response?.data?.detail || 'Une erreur est survenue');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handlePayPalApprove = async (data) => {
    try {
      await axios.post(`${API}/paypal/capture?order_id=${orderId}&paypal_order_id=${data.orderID}`);
      clearCart();
      toast.success('Paiement PayPal réussi !');
      navigate('/checkout/success?method=paypal');
    } catch (error) {
      // Error handled silently
      toast.error('Erreur lors du paiement PayPal');
    }
  };
  
  if (items.length === 0) {
    return (
      <div className="min-h-screen pt-20 lg:pt-24 flex items-center justify-center" data-testid="checkout-empty">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-text_primary mb-4">Votre panier est vide</h2>
          <button
            onClick={() => navigate('/products')}
            className="px-6 py-3 bg-primary text-white rounded-full font-medium hover:bg-primary_hover transition-colors"
            data-testid="go-to-products-button"
          >
            Voir les produits
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen pt-20 lg:pt-24 pb-12" data-testid="checkout-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-text_secondary hover:text-text_primary transition-colors mb-6"
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5" />
          Retour
        </button>
        
        <h1 className="text-3xl font-bold font-heading text-text_primary mb-8" data-testid="checkout-title">
          {t('checkoutTitle')}
        </h1>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-8" data-testid="checkout-form">
              {/* Customer Info */}
              <div className="bg-white p-6 rounded-2xl shadow-sm">
                <h2 className="text-lg font-semibold text-text_primary mb-4">{t('customerInfo')}</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-text_primary mb-2">{t('fullName')} *</label>
                    <input
                      type="text"
                      name="customer_name"
                      value={formData.customer_name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                      data-testid="checkout-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text_primary mb-2">{t('email')} *</label>
                    <input
                      type="email"
                      name="customer_email"
                      value={formData.customer_email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                      data-testid="checkout-email-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text_primary mb-2">{t('phone')} *</label>
                    <input
                      type="tel"
                      name="customer_phone"
                      value={formData.customer_phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                      data-testid="checkout-phone-input"
                    />
                  </div>
                </div>
              </div>
              
              {/* Delivery Info */}
              <div className="bg-white p-6 rounded-2xl shadow-sm">
                <h2 className="text-lg font-semibold text-text_primary mb-4">{t('deliveryAddress')}</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-text_primary mb-2">{t('deliveryAddress')} *</label>
                    <input
                      type="text"
                      name="delivery_address"
                      value={formData.delivery_address}
                      onChange={handleChange}
                      required
                      placeholder="Rue, numéro, boîte"
                      className="w-full px-4 py-3 rounded-xl border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                      data-testid="checkout-address-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text_primary mb-2">{t('city')} *</label>
                    <input
                      type="text"
                      name="delivery_city"
                      value={formData.delivery_city}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                      data-testid="checkout-city-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text_primary mb-2">{t('postalCode')} *</label>
                    <input
                      type="text"
                      name="delivery_postal"
                      value={formData.delivery_postal}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                      data-testid="checkout-postal-input"
                    />
                  </div>
                </div>
              </div>
              
              {/* Payment Method */}
              <div className="bg-white p-6 rounded-2xl shadow-sm">
                <h2 className="text-lg font-semibold text-text_primary mb-4">{t('paymentMethod')}</h2>
                <div className="space-y-3">
                  <label className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    formData.payment_method === 'stripe' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                  }`}>
                    <input
                      type="radio"
                      name="payment_method"
                      value="stripe"
                      checked={formData.payment_method === 'stripe'}
                      onChange={handleChange}
                      className="w-5 h-5 text-primary"
                      data-testid="payment-stripe"
                    />
                    <CreditCard className="w-6 h-6 text-primary" />
                    <div>
                      <p className="font-medium text-text_primary">{t('creditCard')}</p>
                      <p className="text-sm text-text_secondary">Visa, Mastercard, Apple Pay, Google Pay</p>
                    </div>
                  </label>
                  
                  <label className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    formData.payment_method === 'paypal' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                  }`}>
                    <input
                      type="radio"
                      name="payment_method"
                      value="paypal"
                      checked={formData.payment_method === 'paypal'}
                      onChange={handleChange}
                      className="w-5 h-5 text-primary"
                      data-testid="payment-paypal"
                    />
                    <div className="w-6 h-6 flex items-center justify-center">
                      <span className="text-[#003087] font-bold text-sm">PP</span>
                    </div>
                    <div>
                      <p className="font-medium text-text_primary">{t('paypal')}</p>
                      <p className="text-sm text-text_secondary">Paiement sécurisé via PayPal</p>
                    </div>
                  </label>
                  
                  <label className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    formData.payment_method === 'cash' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                  }`}>
                    <input
                      type="radio"
                      name="payment_method"
                      value="cash"
                      checked={formData.payment_method === 'cash'}
                      onChange={handleChange}
                      className="w-5 h-5 text-primary"
                      data-testid="payment-cash"
                    />
                    <Truck className="w-6 h-6 text-secondary_hover" />
                    <div>
                      <p className="font-medium text-text_primary">{t('cashOnDelivery')}</p>
                      <p className="text-sm text-text_secondary">Payez en espèces à la livraison</p>
                    </div>
                  </label>
                </div>
              </div>
              
              {/* Submit Button - For non-PayPal */}
              {formData.payment_method !== 'paypal' && (
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-primary text-white font-semibold rounded-full hover:bg-primary_hover transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="place-order-button"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      {t('processing')}
                    </>
                  ) : (
                    t('placeOrder')
                  )}
                </button>
              )}
              
              {/* PayPal Buttons */}
              {formData.payment_method === 'paypal' && (
                <div className="space-y-4">
                  {!showPayPal ? (
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-[#003087] text-white font-semibold rounded-full hover:bg-[#001f5f] transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="paypal-continue-button"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          {t('processing')}
                        </>
                      ) : (
                        'Continuer avec PayPal'
                      )}
                    </button>
                  ) : (
                    <PayPalScriptProvider options={{ 'client-id': PAYPAL_CLIENT_ID, currency: 'EUR' }}>
                      <PayPalButtons
                        style={{ layout: 'vertical' }}
                        createOrder={(data, actions) => {
                          return actions.order.create({
                            purchase_units: [{
                              amount: { value: total.toFixed(2), currency_code: 'EUR' },
                              description: 'AfriOne B&D - Commande'
                            }]
                          });
                        }}
                        onApprove={handlePayPalApprove}
                        onError={(err) => {
                          // Error handled silently
                          toast.error('Erreur PayPal');
                        }}
                      />
                    </PayPalScriptProvider>
                  )}
                </div>
              )}
            </form>
          </div>
          
          {/* Order Summary */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white p-6 rounded-2xl shadow-sm sticky top-24"
            >
              <h2 className="text-lg font-semibold text-text_primary mb-4">Résumé de la commande</h2>
              
              <ul className="space-y-3 mb-6">
                {items.map(item => (
                  <li key={item.product.id} className="flex gap-3" data-testid={`summary-item-${item.product.id}`}>
                    <img
                      src={item.product.image_url}
                      alt={getLocalizedField(item.product, 'name')}
                      className="w-16 h-16 object-cover rounded-lg"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-text_primary line-clamp-1">
                        {getLocalizedField(item.product, 'name')}
                      </p>
                      <p className="text-sm text-text_secondary">Qty: {item.quantity}</p>
                    </div>
                    <span className="font-medium text-text_primary">
                      €{(item.product.price * item.quantity).toFixed(2)}
                    </span>
                  </li>
                ))}
              </ul>
              
              <div className="border-t border-border pt-4 space-y-2">
                <div className="flex justify-between text-text_secondary">
                  <span>{t('subtotal')}</span>
                  <span>€{subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-text_secondary">
                  <span>{t('deliveryFee')}</span>
                  <span>€{deliveryFee.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold text-text_primary pt-2 border-t border-border">
                  <span>{t('total')}</span>
                  <span className="text-primary" data-testid="checkout-total">€{total.toFixed(2)}</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
